import { getAssignableClients } from './cohortClientEligibilityService';
import { getCohortFilterLabels, sortCohortsByOrder } from './cohortService';

/** @typedef {import('../entities/CareModelCohort.types').CareModelCohort} CareModelCohort */
/** @typedef {import('../entities/CareModelCohort.types').Client} Client */
/** @typedef {
 *   import('../entities/CareModelCohortAssignment.types').CareModelCohortMatch
 * } CareModelCohortMatch */
/** @typedef {
 *   import('../entities/CareModelCohortAssignment.types').CareModelCohortAssignmentReview
 * } CareModelCohortAssignmentReview */
/** @typedef {
 *   import('../entities/CareModelCohortAssignment.types').SuggestedCareModelCohortAssignment
 * } SuggestedCareModelCohortAssignment */
/** @typedef {
 *   import('../entities/CareModelCohortAssignment.types').ManualCareModelCohortAssignment
 * } ManualCareModelCohortAssignment */
/** @typedef {
 *   import('../entities/CareModelCohortAssignment.types').CareModelCohortClientAssignment
 * } CareModelCohortClientAssignment */

/**
 * Return only unassigned clients.
 * @param {Client[]} clients
 * @returns {Client[]}
 */
export function getUnassignedClients(clients = []) {
  return clients.filter((client) => !client.careModelCohort);
}

export function getAssignedClients(clients = []) {
  return clients.filter((client) => client.careModelCohort);
}

/**
 * Build cohort match object.
 * @param {CareModelCohort} cohort
 * @param {Object} hydratedFilters
 * @returns {CareModelCohortMatch}
 */
export function buildCohortMatch(cohort, hydratedFilters = {}) {
  return {
    cohort,
    matchedLabels: getCohortFilterLabels(hydratedFilters),
  };
}

/**
 * Build matched labels map keyed by cohort id.
 * @param {CareModelCohortMatch[]} matches
 * @returns {Record<string, string[]>}
 */
export function buildMatchedLabelsByCohortId(matches = []) {
  return matches.reduce((acc, match) => {
    acc[match.cohort.id] = match.matchedLabels || [];
    return acc;
  }, {});
}

/**
 * Sort cohorts by order for assignment review.
 * @param {CareModelCohort[]} cohorts
 * @returns {CareModelCohort[]}
 */
export function sortCohortsForReview(cohorts = []) {
  return sortCohortsByOrder(cohorts);
}

/**
 * Build review tasks for review & assign flow.
 * Suggested = client matches exactly one cohort.
 * Manual = client matches none or multiple cohorts.
 * Clients not eligible for assignment are left out of both.
 *
 * @param {{
 *   clients?: Client[],
 *   getMatchesForClient: (client: Client) => CareModelCohortMatch[],
 * }} params
 * @returns {CareModelCohortAssignmentReview}
 */
export function buildCohortAssignmentReview({
  clients = [],
  getMatchesForClient,
}) {
  const assignableClients = getAssignableClients(clients);
  const unassignedClients = getUnassignedClients(assignableClients);
  const assignedClients = getAssignedClients(assignableClients);

  const review = {
    suggestedAssignments: [],
    manualAssignments: [],
  };

  unassignedClients.forEach((client) => {
    const matches = getMatchesForClient(client);
    if (matches.length === 1) {
      review.suggestedAssignments.push({
        client,
        currentCohortId: null,
        cohort: matches[0].cohort,
        matchedLabels: matches[0].matchedLabels,
        selected: true,
      });

      return;
    }

    review.manualAssignments.push({
      client,
      currentCohortId: null,
      matchedCohorts: matches.map((item) => item.cohort),
      matchedLabelsByCohortId: buildMatchedLabelsByCohortId(matches),
      selectedCohortId: null,
    });
  });

  assignedClients.forEach((client) => {
    const matches = getMatchesForClient(client);

    if (matches.length === 0) {
      return;
    }

    const currentCohortIndex = matches.findIndex(
      ({ cohort }) => cohort.id === client.careModelCohort,
    );

    const suggestedMatch = matches[0];

    const hasHigherPriorityMatch = currentCohortIndex === -1
      ? suggestedMatch.cohort.id !== client.careModelCohort
      : currentCohortIndex > 0;

    if (!hasHigherPriorityMatch) {
      return;
    }

    review.suggestedAssignments.push({
      client,
      currentCohortId: client.careModelCohort,
      cohort: suggestedMatch.cohort,
      matchedLabels: suggestedMatch.matchedLabels,
      selected: true,
    });
  });

  return review;
} // buildCohortAssignmentReview()

/**
 * Collect confirmed assignments from review state.
 *
 * @param {{
 *   suggestedAssignments?: SuggestedCareModelCohortAssignment[],
 *   manualAssignments?: ManualCareModelCohortAssignment[],
 * }} params
 * @returns {CareModelCohortClientAssignment[]}
 */
export function collectConfirmedAssignments({
  suggestedAssignments = [],
  manualAssignments = [],
}) {
  return [
    ...suggestedAssignments
      .filter((item) => item.selected)
      .map((item) => ({
        clientId: item.client.id,
        cohortId: item.cohort.id,
      })),
    ...manualAssignments
      .filter((item) => !!item.selectedCohortId)
      .map((item) => ({
        clientId: item.client.id,
        cohortId: item.selectedCohortId,
      })),
  ];
}

/**
 * Count clients the review & assign flow has something to offer: unassigned ones plus the
 * assigned ones a higher priority cohort now matches.
 *
 * @param {{
 *   clients?: Client[],
 *   impactedClients?: SuggestedCareModelCohortAssignment[],
 * }} params
 * @returns {number}
 */
export function countClientsNeedingCohortReview({
  clients = [],
  impactedClients = [],
}) {
  const clientIds = new Set(
    getUnassignedClients(getAssignableClients(clients)).map((client) => client.id),
  );

  impactedClients.forEach(({ client }) => {
    if (client?.id) {
      clientIds.add(client.id);
    }
  });

  return clientIds.size;
} // countClientsNeedingCohortReview()
